import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import ApplicationPage from "./pages/ApplicationPage";
import Dashboard from "./pages/Dashboard";
import AdminPanel from "./pages/AdminPanel";
import EventLab from "./pages/EventLab";
import Navbar, { ASSETS } from "./components/Navbar";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <img 
          src="https://customer-assets.emergentagent.com/job_destiny-match-9/artifacts/ub6fc5ad_WhatsApp%20Image%202026-03-26%20at%208.22.14%20PM%20%281%29.jpeg"
          alt="Loading..."
          className="w-20 h-20 logo-glow animate-pulse object-contain"
        />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !['admin', 'super_admin'].includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router Component
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Application (requires auth but not approval) */}
      <Route 
        path="/apply" 
        element={
          <ProtectedRoute>
            <ApplicationPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Application Status */}
      <Route 
        path="/application-status" 
        element={
          <ProtectedRoute>
            <ApplicationStatus />
          </ProtectedRoute>
        } 
      />
      
      {/* Dashboard (requires auth) */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Panel (requires admin role) */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute adminOnly>
            <AdminPanel />
          </ProtectedRoute>
        } 
      />
      
      {/* Event Lab (requires admin role) */}
      <Route 
        path="/lab" 
        element={
          <ProtectedRoute adminOnly>
            <EventLab />
          </ProtectedRoute>
        } 
      />
      
      {/* Payment routes */}
      <Route 
        path="/payment/success" 
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/payment/cancel" 
        element={
          <ProtectedRoute>
            <PaymentCancel />
          </ProtectedRoute>
        } 
      />
      
      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Application Status Page
const ApplicationStatus = () => {
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen bg-[#0B0B0B]">
      <Navbar />
      <div className="pt-40 pb-20 px-6 max-w-2xl mx-auto text-center">
        <img 
          src="https://customer-assets.emergentagent.com/job_destiny-match-9/artifacts/ub6fc5ad_WhatsApp%20Image%202026-03-26%20at%208.22.14%20PM%20%281%29.jpeg"
          alt="Soul Mate Dates"
          className="w-20 h-20 mx-auto mb-10 logo-glow object-contain"
        />
        <h1 className="text-4xl font-serif text-[#F5F0E6] mb-4">
          Aplicación Enviada
        </h1>
        <p className="text-[#8A8A8A] text-lg mb-10">
          Tu aplicación está siendo revisada por nuestro equipo. 
          Te notificaremos cuando tengamos una decisión.
        </p>
        <div className="card-premium p-8 text-left">
          <h3 className="text-lg font-serif text-[#F5F0E6] mb-6">¿Qué sigue?</h3>
          <ol className="space-y-5 text-[#8A8A8A]">
            <li className="flex items-start gap-4">
              <span className="text-[#D4AF37] font-serif text-xl">1.</span>
              <span>Nuestro equipo revisará tu perfil y respuestas</span>
            </li>
            <li className="flex items-start gap-4">
              <span className="text-[#D4AF37] font-serif text-xl">2.</span>
              <span>La IA analizará tu compatibilidad potencial</span>
            </li>
            <li className="flex items-start gap-4">
              <span className="text-[#D4AF37] font-serif text-xl">3.</span>
              <span>Recibirás una notificación con nuestra decisión</span>
            </li>
            <li className="flex items-start gap-4">
              <span className="text-[#D4AF37] font-serif text-xl">4.</span>
              <span>Si eres aprobado/a, tendrás acceso a eventos exclusivos</span>
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
};

// Payment Success Page
const PaymentSuccess = () => {
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      const checkStatus = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`,
            { credentials: 'include' }
          );
          const data = await response.json();
          console.log('Payment status:', data);
        } catch (error) {
          console.error('Error checking payment:', error);
        }
      };
      checkStatus();
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0B0B]">
      <Navbar />
      <div className="pt-40 pb-20 px-6 max-w-2xl mx-auto text-center">
        <div className="w-20 h-20 mx-auto mb-10 bg-emerald-900/30 border border-emerald-500/50 flex items-center justify-center">
          <svg className="w-10 h-10 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-4xl font-serif text-[#F5F0E6] mb-4">
          ¡Pago Exitoso!
        </h1>
        <p className="text-[#8A8A8A] text-lg mb-10">
          Tu pago ha sido procesado correctamente. 
          Ya puedes disfrutar de tus beneficios.
        </p>
        <a href="/dashboard" className="btn-gold inline-block" data-testid="go-to-dashboard">
          Ir al Dashboard
        </a>
      </div>
    </div>
  );
};

// Payment Cancel Page
const PaymentCancel = () => {
  return (
    <div className="min-h-screen bg-[#0B0B0B]">
      <Navbar />
      <div className="pt-40 pb-20 px-6 max-w-2xl mx-auto text-center">
        <div className="w-20 h-20 mx-auto mb-10 bg-red-900/30 border border-red-500/50 flex items-center justify-center">
          <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h1 className="text-4xl font-serif text-[#F5F0E6] mb-4">
          Pago Cancelado
        </h1>
        <p className="text-[#8A8A8A] text-lg mb-10">
          El proceso de pago fue cancelado. 
          Puedes intentarlo de nuevo cuando quieras.
        </p>
        <a href="/dashboard" className="btn-outline-gold inline-block">
          Volver al Dashboard
        </a>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
